import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, FieldWithLabel, Heading, Input, Textarea } from '@app/shared/ui/atoms/Common';
import { StoryRow } from '@app/shared/ui/molecules/Admin/MatrixChannelForm';
import { ConfirmationModal } from '@app/shared/ui/organisms/Common';
import { useAdminMatrixChannelFormTranslations } from '@app/shared/hooks/admin/useAdminMatrixChannelFormTranslations';
import { capitalize } from '@app/shared/utils/common';
import { Story, StoryWithId } from '@app/shared/types/admin/pages/matrixChannelForm';

interface StoriesSectionProps {
  stories: StoryWithId[];
  onStoryCreate: (story: Story) => void;
  onStoryUpdate: (id: string, story: Story) => void;
  onStoryRemove: (id: string) => void;
}

export function StoriesSection({
  stories,
  onStoryCreate,
  onStoryUpdate,
  onStoryRemove,
}: StoriesSectionProps) {
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<Story>();
  const [storyToRemove, setStoryToRemove] = useState<StoryWithId | null>(null);
  const { t } = useTranslation();
  const { getTranslation, requiredError } = useAdminMatrixChannelFormTranslations();

  const handleStoryCreate = (values: Story) => {
    onStoryCreate(values);
    reset();
  };

  const resetStoryToRemove = () => {
    setStoryToRemove(null);
  };

  const handleStoryRemove = () => {
    if (storyToRemove) {
      onStoryRemove(storyToRemove.id);
      resetStoryToRemove();
    }
  };

  const storiesLabel = getTranslation('stories');
  const titleLabel = getTranslation('title_optional');
  const titlePlaceholder = getTranslation('title');
  const descriptionLabel = capitalize(getTranslation('description'));
  const descriptionRequiredErrorMessage = requiredError('description');
  const addStoryLabel = getTranslation('add_story');
  const storyLabel = getTranslation('story');

  return (
    <section className="flex flex-col gap-4">
      <Heading level={2} className="text-center">
        {storiesLabel}
      </Heading>

      <form onSubmit={handleSubmit(handleStoryCreate)}>
        <FieldWithLabel label={titleLabel}>
          <Input {...register('title')} placeholder={titlePlaceholder} />
        </FieldWithLabel>
        <FieldWithLabel label={descriptionLabel} hasErrorField error={errors.description?.message}>
          <Textarea
            {...register('description', {
              required: { value: true, message: descriptionRequiredErrorMessage },
            })}
            placeholder={descriptionLabel}
          />
        </FieldWithLabel>
        <Button>{addStoryLabel}</Button>
      </form>

      <div className="flex flex-col gap-2">
        {stories.map((item) => (
          <StoryRow
            key={item.id}
            story={item}
            onUpdate={(values) => onStoryUpdate(item.id, values)}
            onRemove={() => setStoryToRemove(item)}
          />
        ))}
      </div>

      <ConfirmationModal
        isOpen={!!storyToRemove}
        onOpenChange={resetStoryToRemove}
        actionType="delete"
        withCustomTitle={false}
        actionName={t('common.delete')}
        itemType={storyLabel}
        itemName={storyToRemove?.title || ''}
        onCancel={resetStoryToRemove}
        onConfirm={handleStoryRemove}
      />
    </section>
  );
}
